import { LineItemTargetValue, Maybe } from '../../../apis/graphql/types';
import { TargetOption } from '../../../models/Targeting';
import { AGE, AGE_TITLE, GENDER, GENDER_TITLE, TARGETING_AGE_NAME } from '../constants/ageTargetings';
import { AgesAndGenders, AgesTargetingType, isTargetingAgeName } from '../Form/types';

export const createAgeTargetingsToSave = (
  chosenAgeTargetingNames: TARGETING_AGE_NAME[],
  ageTargetings: TargetOption[]
): AgesTargetingType[] => {
  return chosenAgeTargetingNames.reduce((targetData: AgesTargetingType[], targetingAgeName: TARGETING_AGE_NAME) => {
    const targetId = findTargetingIdByAgeTargetingName(targetingAgeName, ageTargetings);

    return targetId
      ? [...targetData, { id: targetId, isExclusion: false, targetingName: targetingAgeName }]
      : targetData;
  }, []);
};

const findTargetingIdByAgeTargetingName = (
  ageTargetingName: TARGETING_AGE_NAME,
  allAgeTargetings: TargetOption[]
): string | null => {
  return allAgeTargetings.find((ageTargeting) => ageTargeting.name === ageTargetingName)?.value || null;
};
export const getAgeTargetingNames = (ages: AGE[], gender?: GENDER): TARGETING_AGE_NAME[] => {
  if (!gender && !ages.length) {
    return [];
  }

  let chosenAges: AGE[] = ages.length ? ages : [AGE.from18to24, AGE.from25to34, AGE.from35to49, AGE.from50];
  let chosenGender: GENDER = gender || GENDER.all;

  return chosenAges.map((chosenAge: AGE) => getTargetingAgeNameByAgeAndGender(chosenGender, chosenAge));
};

export const getTargetingAgeNameByAgeAndGender = (gender: GENDER, age: AGE): TARGETING_AGE_NAME => {
  switch (gender) {
    case GENDER.all:
      return getAdultTargetingAgeName(age);
    case GENDER.female:
      return getFemaleTargetingAgeName(age);
    case GENDER.male:
      return getMaleTargetingAgeName(age);
  }
};

const getAdultTargetingAgeName = (age: AGE): TARGETING_AGE_NAME => {
  switch (age) {
    case AGE.from18to24:
      return TARGETING_AGE_NAME.adults18to24;
    case AGE.from21to34:
      return TARGETING_AGE_NAME.adults21to34;
    case AGE.from25to34:
      return TARGETING_AGE_NAME.adults25to34;
    case AGE.from35to49:
      return TARGETING_AGE_NAME.adults35to49;
    case AGE.from50:
      return TARGETING_AGE_NAME.adults50up;
  }
};

const getFemaleTargetingAgeName = (age: AGE): TARGETING_AGE_NAME => {
  switch (age) {
    case AGE.from18to24:
      return TARGETING_AGE_NAME.females18to24;
    case AGE.from21to34:
      return TARGETING_AGE_NAME.females21to34;
    case AGE.from25to34:
      return TARGETING_AGE_NAME.females25to34;
    case AGE.from35to49:
      return TARGETING_AGE_NAME.females35to49;
    case AGE.from50:
      return TARGETING_AGE_NAME.females50up;
  }
};

const getMaleTargetingAgeName = (age: AGE): TARGETING_AGE_NAME => {
  switch (age) {
    case AGE.from18to24:
      return TARGETING_AGE_NAME.males18to24;
    case AGE.from21to34:
      return TARGETING_AGE_NAME.males21to34;
    case AGE.from25to34:
      return TARGETING_AGE_NAME.males25to34;
    case AGE.from35to49:
      return TARGETING_AGE_NAME.males35to49;
    case AGE.from50:
      return TARGETING_AGE_NAME.males50up;
  }
};

export const getAgesAndGendersByAgesTargetsValues = (ages: AgesTargetingType[]): AgesAndGenders => {
  const targetingAgeNames: TARGETING_AGE_NAME[] = [];
  ages.forEach((age) => {
    if (age.targetingName && isTargetingAgeName(age.targetingName)) {
      targetingAgeNames.push(age.targetingName);
    }
  });

  return getAgesAndGenderByTargetingAgeNames(targetingAgeNames);
};
export const getAgesAndGenderByLineItemTargetings = (
  lineItemTargetings: Maybe<Array<Maybe<LineItemTargetValue>>>
): AgesAndGenders => {
  if (!lineItemTargetings) {
    return {
      ages: [],
      genders: [],
    };
  }
  const targetingAgeNames: TARGETING_AGE_NAME[] = [];
  lineItemTargetings.forEach((lineItemTargeting) => {
    if (lineItemTargeting?.originalName && isTargetingAgeName(lineItemTargeting.originalName)) {
      targetingAgeNames.push(lineItemTargeting.originalName);
    }
  });

  return getAgesAndGenderByTargetingAgeNames(targetingAgeNames);
};
export const getAgesAndGenderByTargetingAgeNames = (targetingAgeNames: TARGETING_AGE_NAME[]): AgesAndGenders => {
  return targetingAgeNames.reduce(
    (agesAndGender: AgesAndGenders, targetingAgeName: TARGETING_AGE_NAME) => {
      const targetingAgeAndGender = getGenderAndAgeByTargetingAgeName(targetingAgeName);
      if (!targetingAgeAndGender) return agesAndGender;

      if (!agesAndGender.genders.includes(targetingAgeAndGender.gender)) {
        agesAndGender.genders.push(targetingAgeAndGender.gender);
      }

      if (!agesAndGender.ages.includes(targetingAgeAndGender.age)) {
        agesAndGender.ages.push(targetingAgeAndGender.age);
      }

      return agesAndGender;
    },
    {
      ages: [],
      genders: [],
    }
  );
};

export const getGenderAndAgeByTargetingAgeName = (
  targetingAgeName: TARGETING_AGE_NAME
): {
  age: AGE;
  gender: GENDER;
} | null => {
  switch (targetingAgeName) {
    case TARGETING_AGE_NAME.adults18to24:
      return {
        age: AGE.from18to24,
        gender: GENDER.all,
      };
    case TARGETING_AGE_NAME.adults21to34:
      return {
        age: AGE.from21to34,
        gender: GENDER.all,
      };
    case TARGETING_AGE_NAME.adults25to34:
      return {
        age: AGE.from25to34,
        gender: GENDER.all,
      };
    case TARGETING_AGE_NAME.adults35to49:
      return {
        age: AGE.from35to49,
        gender: GENDER.all,
      };
    case TARGETING_AGE_NAME.adults50up:
      return {
        age: AGE.from50,
        gender: GENDER.all,
      };

    case TARGETING_AGE_NAME.females18to24:
      return {
        age: AGE.from18to24,
        gender: GENDER.female,
      };
    case TARGETING_AGE_NAME.females21to34:
      return {
        age: AGE.from21to34,
        gender: GENDER.female,
      };
    case TARGETING_AGE_NAME.females25to34:
      return {
        age: AGE.from25to34,
        gender: GENDER.female,
      };
    case TARGETING_AGE_NAME.females35to49:
      return {
        age: AGE.from35to49,
        gender: GENDER.female,
      };
    case TARGETING_AGE_NAME.females50up:
      return {
        age: AGE.from50,
        gender: GENDER.female,
      };

    case TARGETING_AGE_NAME.males18to24:
      return {
        age: AGE.from18to24,
        gender: GENDER.male,
      };
    case TARGETING_AGE_NAME.males21to34:
      return {
        age: AGE.from21to34,
        gender: GENDER.male,
      };
    case TARGETING_AGE_NAME.males25to34:
      return {
        age: AGE.from25to34,
        gender: GENDER.male,
      };
    case TARGETING_AGE_NAME.males35to49:
      return {
        age: AGE.from35to49,
        gender: GENDER.male,
      };
    case TARGETING_AGE_NAME.males50up:
      return {
        age: AGE.from50,
        gender: GENDER.male,
      };
    default:
      return null;
  }
};

export const getStringFromAges = (ages: AGE[]): string =>
  ages
    .map((age): string => AGE_TITLE[age])
    .sort((a, b) => ('' + a).localeCompare(b))
    .join(', ');

export const getStringFromGenders = (gender: GENDER[]): string =>
  gender.map((gender): string => GENDER_TITLE[gender]).join(', ');

export const getAllowedGenders = (ageTargetingOptions: TargetOption[]): GENDER[] => {
  const allowedGenders: GENDER[] = [];

  ageTargetingOptions.forEach((ageTargetingOption: TargetOption) => {
    if (isTargetingAgeName(ageTargetingOption.name)) {
      const ageAndGender = getGenderAndAgeByTargetingAgeName(ageTargetingOption.name);

      if (ageAndGender && !allowedGenders.includes(ageAndGender.gender)) {
        allowedGenders.push(ageAndGender.gender);
      }
    }
  });

  return allowedGenders;
};
